import { useEffect, useMemo, useRef, useState } from 'react'

interface UseTypewriterOptions {
  enabled?: boolean
  speed?: number // base ms per character
  startDelay?: number // initial delay before typing starts
  loop?: boolean
  pauseDelay?: number // delay before restarting when looping
  // Humanize typing behavior
  speedJitter?: number // 0..1 fraction to vary around base speed
  spaceFactor?: number // multiplier for spaces (faster)
  commaExtraDelay?: number // extra ms after , ; :
  punctuationExtraDelay?: number // extra ms after . ! ? and newlines
  midWordSlowdown?: number // 0..1 slowdown amplitude in middle of words
}

export const useTypewriter = (
  text: string,
  {
    enabled = true,
    speed = 45,
    startDelay = 250,
    loop = false,
    pauseDelay = 1200,
    speedJitter = 0.35,
    spaceFactor = 0.6,
    commaExtraDelay = 120,
    punctuationExtraDelay = 400,
    midWordSlowdown = 0.35,
  }: UseTypewriterOptions = {}
) => {
  const [output, setOutput] = useState('')
  const indexRef = useRef(0)
  const timerRef = useRef<number | null>(null)

  // Normalize the input text to a stable value
  const target = useMemo(() => text ?? '', [text])

  useEffect(() => {
    // Reset when text changes or when disabled
    indexRef.current = 0
    setOutput('')

    if (!enabled) {
      // While disabled (e.g., user typing), keep placeholder empty to avoid flashes
      setOutput('')
      return
    }

    const start = window.setTimeout(() => {
      const isWordChar = (c: string) => /[A-Za-z0-9]/.test(c)

      const delayForIndex = (i: number) => {
        const ch = target.charAt(i)
        // Base and space handling
        let base = speed
        if (ch === ' ') base = Math.max(10, Math.round(base * spaceFactor))

        // Jitter
        const jitter = Math.max(0, Math.min(1, speedJitter)) * base
        let varied = base - jitter + Math.random() * (2 * jitter)

        // Mid-word slowdown: slow in the center of a word, not between words
        if (isWordChar(ch)) {
          let startIdx = i
          while (startIdx - 1 >= 0 && isWordChar(target.charAt(startIdx - 1))) {
            startIdx -= 1
          }
          let endIdx = i
          while (
            endIdx + 1 < target.length &&
            isWordChar(target.charAt(endIdx + 1))
          ) {
            endIdx += 1
          }
          const len = endIdx - startIdx + 1
          if (len > 1) {
            const pos = (i - startIdx) / (len - 1) // 0..1
            // Parabolic peak at center: 1 - 4*(x-0.5)^2 in [0,1]
            const centerEmphasis = 1 - 4 * Math.pow(pos - 0.5, 2)
            const clamped = Math.max(0, centerEmphasis)
            const amplitude = Math.max(0, Math.min(1, midWordSlowdown))
            const factor = 1 + amplitude * clamped
            varied *= factor
          }
        }

        // Punctuation pauses
        let extra = 0
        if (/\n|[.!?]/.test(ch)) extra = punctuationExtraDelay
        else if (/[,:;]/.test(ch)) extra = commaExtraDelay
        return Math.max(10, Math.round(varied + extra))
      }

      const step = () => {
        if (indexRef.current < target.length) {
          indexRef.current += 1
          setOutput(target.slice(0, indexRef.current))
          const idx = indexRef.current - 1
          timerRef.current = window.setTimeout(step, delayForIndex(idx))
        } else if (loop) {
          timerRef.current = window.setTimeout(() => {
            indexRef.current = 0
            setOutput('')
            const firstIdx = 0
            timerRef.current = window.setTimeout(step, delayForIndex(firstIdx))
          }, pauseDelay)
        }
      }

      step()
    }, startDelay)

    return () => {
      window.clearTimeout(start)
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [
    target,
    enabled,
    speed,
    startDelay,
    loop,
    pauseDelay,
    speedJitter,
    spaceFactor,
    commaExtraDelay,
    punctuationExtraDelay,
    midWordSlowdown,
  ])

  return output
}
